import React, { useState, useEffect, useCallback } from "react";
import { motion } from "motion/react";
import {
  Target, MessageSquare, Briefcase, CheckCircle,
  Save, Loader2, AlertTriangle
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAutoSave } from "../../hooks/useAutoSave";
import AutoSaveIndicator from "./AutoSaveIndicator";

export default function ClientSessionBrief({ booking, expertSpecializations, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    focus_area: "",
    task_description: "",
    current_situation: "",
    expected_outcome: ""
  });

  const specs = expertSpecializations || [];

  const outcomeOptions = [
    "Конкретный план действий",
    "Экспертная оценка идеи или решения",
    "Разбор проблемы и поиск причин",
    "Стратегические рекомендации",
    "Другое"
  ];

  const buildPayload = useCallback((formData) => ({
    focus_area: formData.focus_area,
    task_description: formData.task_description,
    current_situation: formData.current_situation,
    expected_outcome: formData.expected_outcome
  }), []);

  const buildInsertPayload = useCallback((formData) => ({
    focus_area: formData.focus_area,
    task_description: formData.task_description,
    current_situation: formData.current_situation,
    expected_outcome: formData.expected_outcome,
    booking_id: booking.id,
    client_id: booking.clientId,
    expert_id: booking.expertId
  }), [booking.id, booking.clientId, booking.expertId]);

  const {
    autoSaving,
    lastAutoSaved,
    restoredFromDraft,
    setRestoredFromDraft,
    scheduleAutoSave,
    cancelAutoSave,
    markSaved,
    loadFromLocal
  } = useAutoSave({
    table: "client_session_briefs",
    bookingId: booking.id,
    form,
    buildPayload,
    buildInsertPayload,
    enabled: !loading
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await supabase
          .from("client_session_briefs")
          .select("*")
          .eq("booking_id", booking.id)
          .maybeSingle();
        if (data) {
          setForm({
            focus_area: data.focus_area || "",
            task_description: data.task_description || "",
            current_situation: data.current_situation || "",
            expected_outcome: data.expected_outcome || ""
          });
        } else {
          const localDraft = loadFromLocal();
          if (localDraft) {
            setForm(localDraft);
            setRestoredFromDraft(true);
          }
        }
      } catch (err) {
        console.warn("Failed to load client brief:", err);
        const localDraft = loadFromLocal();
        if (localDraft) {
          setForm(localDraft);
          setRestoredFromDraft(true);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [booking.id, loadFromLocal, setRestoredFromDraft]);

  const handleSave = async () => {
    cancelAutoSave();
    setSaving(true);
    setError("");
    try {
      const { data: existing } = await supabase
        .from("client_session_briefs")
        .select("id")
        .eq("booking_id", booking.id)
        .maybeSingle();

      const payload = {
        focus_area: form.focus_area,
        task_description: form.task_description,
        current_situation: form.current_situation,
        expected_outcome: form.expected_outcome,
        updated_at: new Date().toISOString()
      };

      if (existing) {
        await supabase
          .from("client_session_briefs")
          .update(payload)
          .eq("id", existing.id);
      } else {
        await supabase
          .from("client_session_briefs")
          .insert({
            ...payload,
            booking_id: booking.id,
            client_id: booking.clientId,
            expert_id: booking.expertId
          });
      }

      markSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onSaved) onSaved(form);
    } catch (err) {
      setError("Не удалось сохранить. Попробуйте ещё раз.");
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => {
    const newForm = { ...form, [field]: value };
    setForm(newForm);
    scheduleAutoSave(newForm);
  };

  if (loading) {
    return (
      <div id="client-session-brief_loading" className="p-6 text-center">
        <Loader2 className="w-6 h-6 text-primary-400 animate-spin mx-auto" />
      </div>
    );
  }

  const hasContent = form.task_description.trim().length > 0;

  return (
    <motion.div
      id="client-session-brief_root"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-md bg-gradient-to-br from-primary-950/30 to-accent-950/10 border border-primary-600/20"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary-600/20 flex items-center justify-center">
            <Target className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h3 className="text-xl font-medium text-white">
              Подготовка к встрече
            </h3>
            <p className="text-lg font-extralight text-neutral-400">
              Помогите эксперту подготовиться к сессии
            </p>
          </div>
        </div>
        <AutoSaveIndicator
          autoSaving={autoSaving}
          lastAutoSaved={lastAutoSaved}
          restoredFromDraft={restoredFromDraft}
          onDismissRestore={() => setRestoredFromDraft(false)}
        />
      </div>

      <div className="space-y-5">
        {specs.length > 0 && (
          <div>
            <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
              <Briefcase className="w-4 h-4 text-primary-400" />
              По какому направлению хотите консультацию?
            </label>
            <div className="flex flex-wrap gap-2">
              {specs.map((spec) => (
                <button
                  key={spec}
                  type="button"
                  onClick={() => update("focus_area", spec)}
                  className={`px-4 py-2 rounded-md text-lg font-extralight transition-all ${
                    form.focus_area === spec
                      ? "bg-primary-600/20 text-primary-300 border border-primary-500/40"
                      : "bg-neutral-800/50 text-neutral-400 border border-neutral-700/50 hover:border-neutral-600"
                  }`}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
            <MessageSquare className="w-4 h-4 text-accent-400" />
            Какую задачу хотите решить с помощью эксперта?
          </label>
          <textarea
            value={form.task_description}
            onChange={(e) => update("task_description", e.target.value)}
            rows={3}
            placeholder="Например: хочу понять, как выстроить маркетинговую стратегию для нового продукта на рынке B2B..."
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500 resize-none transition-colors"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
            <Briefcase className="w-4 h-4 text-blue-400" />
            Кратко опишите текущую ситуацию (опционально)
          </label>
          <textarea
            value={form.current_situation}
            onChange={(e) => update("current_situation", e.target.value)}
            rows={2}
            placeholder="Компания, стадия, команда, бюджет, текущие результаты..."
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500 resize-none transition-colors"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
            <Target className="w-4 h-4 text-green-400" />
            С чем хотите уйти после встречи?
          </label>
          <div className="flex flex-wrap gap-2">
            {outcomeOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => update("expected_outcome", option)}
                className={`px-4 py-2 rounded-md text-lg font-extralight transition-all ${
                  form.expected_outcome === option
                    ? "bg-green-600/20 text-green-300 border border-green-500/40"
                    : "bg-neutral-800/50 text-neutral-400 border border-neutral-700/50 hover:border-neutral-600"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 mt-4 p-3 rounded-md bg-red-500/10 border border-red-500/20">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-lg font-extralight text-red-300">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between mt-6">
        <div>
          {saved && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 text-accent-400"
            >
              <CheckCircle className="w-5 h-5" />
              <span className="text-lg font-extralight">Сохранено</span>
            </motion.div>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasContent}
          className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>
    </motion.div>
  );
}
